/**
 * AgentTraceInline - Reasoning/Thought Process Display
 *
 * Shows AI reasoning steps in a Claude/Gemini/DeepSeek-style collapsible block.
 * Intentionally hides internal tool names, source labels, and data freshness details
 * from the user. Instead shows natural language reasoning steps.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Sparkles,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { C } from '../design-system/primitives/constants';

/**
 * Map internal step types/labels to user-friendly reasoning descriptions.
 * Strips tool names, source labels, row counts, and other internal details.
 */
function humanizeStep(step) {
  const label = (step.label || '').toLowerCase();

  // Skip internal data freshness steps entirely
  if (label.includes('data status') || label.includes('freshness')) return null;
  // Skip "Query received" user step
  if (step.type === 'user') return null;
  // Skip "Complete" terminal step (shown in header)
  if (step.type === 'results' && label === 'complete') return null;

  // Map tool calls to natural language
  if (step.type === 'tool_call') {
    // Prefer traceLabel from unified registry (sent by backend SSE)
    if (step.traceLabel) return step.traceLabel;
    if (step.traceLabel === null) return null; // Explicitly hidden (e.g., data freshness)

    // Fallback: keyword matching for legacy SSE events without traceLabel
    if (label.includes('natstat') || label.includes('player stat') || label.includes('player director')) return 'Looking up player statistics...';
    if (label.includes('team stat') || label.includes('team director')) return 'Pulling team data...';
    if (label.includes('game result') || label.includes('natstat_games')) return 'Checking game results...';
    if (label.includes('statcast') || label.includes('savant')) return 'Analyzing advanced metrics...';
    if (label.includes('espn') || label.includes('news')) return 'Checking latest news...';
    if (label.includes('canvas') || label.includes('scouting') || label.includes('intelligence')) return 'Searching scouting reports...';
    if (label.includes('transfer') || label.includes('portal')) return 'Searching transfer portal...';
    if (label.includes('vertex') || label.includes('knowledge base')) return 'Searching knowledge base...';
    if (label.includes('google') || label.includes('search') || label.includes('web')) return 'Searching for latest information...';
    if (label.includes('bigquery') || label.includes('query_sportsdata') || label.includes('nl2sql') || label.includes('sportsdata')) return 'Querying analytics database...';
    if (label.includes('shot') && label.includes('quality')) return 'Analyzing shot quality...';
    if (label.includes('shot') && label.includes('heatmap')) return 'Generating shot chart...';
    if (label.includes('video') || label.includes('film')) return 'Analyzing game film...';
    if (label.includes('file') || label.includes('upload')) return 'Analyzing uploaded file...';
    if (label.includes('url') || label.includes('article')) return 'Reading article content...';
    if (label.includes('exa') || label.includes('research')) return 'Researching across sources...';
    if (label.includes('box score')) return 'Pulling box score data...';
    if (label.includes('roster')) return 'Checking roster information...';
    if (label.includes('evaluate') || label.includes('fit score')) return 'Running player evaluation...';
    if (label.includes('email') || label.includes('report') || label.includes('pdf')) return 'Generating report...';
    if (label.includes('freshness') || label.includes('data status')) return null;
    // Generic fallback — strip "Tool: " prefix
    const cleaned = step.label.replace(/^Tool:\s*/i, '');
    return `Working on: ${cleaned}...`;
  }

  // Map thinking steps
  if (step.type === 'thinking' || step.type === 'reasoning' || step.type === 'intent') {
    if (label.includes('orchestration') && label.includes('complete')) return 'Understood your question';
    if (label.includes('orchestration') || label.includes('planning')) return 'Understanding your question...';
    if (label.includes('analysis') && label.includes('complete')) return 'Analysis complete';
    if (label.includes('analysis') || label.includes('analyzing')) return 'Analyzing...';
    if (label.includes('synthesis') || label.includes('synthesiz')) return 'Putting it all together...';
    if (label.includes('processing')) return 'Processing results...';
    if (label.includes('claude')) return 'Planning approach...';
    if (label.includes('retry')) return 'Refining analysis...';
    // Pass through if already clean
    return step.label;
  }

  // SQL steps
  if (step.type === 'sql') return 'Querying database...';

  // Error steps — show as-is
  if (step.type === 'error') return step.label || 'Something went wrong';

  return step.label || null;
}

/**
 * Single reasoning step — minimal, clean
 */
const ThoughtStep = ({ text, isActive, isError }) => (
  <div className="flex items-center gap-2 py-0.5">
    {isActive ? (
      <Loader2 size={10} className="animate-spin flex-shrink-0" style={{ color: C.gold }} />
    ) : isError ? (
      <AlertCircle size={10} className="flex-shrink-0" style={{ color: '#ef4444' }} />
    ) : (
      <div className="w-2.5 h-2.5 flex-shrink-0 flex items-center justify-center">
        <div className="w-1 h-1 rounded-full" style={{ background: C.t3 }} />
      </div>
    )}
    <span
      className="text-[11px] leading-relaxed"
      style={{ color: isActive ? C.gold : isError ? '#ef4444' : C.t3 }}
    >
      {text}
    </span>
  </div>
);

/**
 * Main AgentTraceInline Component — Reasoning Style
 */
export default function AgentTraceInline({
  steps = [],
  isStreaming = false,
  agentState = {},
  statusMessage = '',
  currentAgentName = 'Agent',
  className = '',
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const startTimeRef = useRef(Date.now());
  const [elapsedMs, setElapsedMs] = useState(0);

  // Auto-expand during streaming, collapse after
  useEffect(() => {
    if (isStreaming) {
      startTimeRef.current = Date.now();
      setIsExpanded(true);
      const interval = setInterval(() => {
        setElapsedMs(Date.now() - startTimeRef.current);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isStreaming]);

  // Don't render if nothing to show
  if (steps.length === 0 && !isStreaming) return null;

  // Transform steps to human-readable thoughts (deduplicated)
  const seen = new Set();
  const thoughts = [];
  for (const step of steps) {
    const text = humanizeStep(step);
    if (!text) continue;
    // Deduplicate — same text should only appear once (unless active)
    const key = text.replace(/\.\.\.$/, '');
    if (seen.has(key) && !step.streaming) continue;
    seen.add(key);
    thoughts.push({ text, isActive: step.streaming, isError: step.type === 'error', id: step.id });
  }

  const isComplete = agentState.status === 'complete';
  const hasError = steps.some(s => s.type === 'error');
  const activeThought = thoughts.find(t => t.isActive);

  // Header text
  const headerText = isStreaming
    ? (activeThought?.text || statusMessage || 'Thinking...')
    : hasError
    ? 'Encountered an issue'
    : isComplete
    ? 'Thought process'
    : 'Thinking...';

  const totalDuration = steps.reduce((sum, s) => sum + (s.duration || 0), 0);
  const displayDuration = isStreaming ? elapsedMs : totalDuration;

  return (
    <div
      className={className}
      style={{
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Header — clickable to expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 text-left cursor-pointer bg-transparent border-0"
        style={{ padding: '6px 0' }}
      >
        {/* Thinking indicator */}
        {isStreaming && !hasError ? (
          <Sparkles size={12} className="flex-shrink-0" style={{ color: C.gold }} />
        ) : hasError ? (
          <AlertCircle size={12} className="flex-shrink-0" style={{ color: '#ef4444' }} />
        ) : (
          <Sparkles size={12} className="flex-shrink-0" style={{ color: C.t3 }} />
        )}

        <span
          className="flex-1 text-[11px] font-medium min-w-0 truncate"
          style={{ color: isStreaming ? C.gold : C.t3 }}
        >
          {headerText}
        </span>

        {/* Duration */}
        {displayDuration > 500 && (
          <span
            className="text-[10px] flex-shrink-0 tabular-nums"
            style={{ color: C.t3 + '99' }}
          >
            {(displayDuration / 1000).toFixed(1)}s
          </span>
        )}

        {/* Expand/collapse chevron */}
        {thoughts.length > 0 && (
          isExpanded
            ? <ChevronUp size={11} style={{ color: C.t3, flexShrink: 0 }} />
            : <ChevronDown size={11} style={{ color: C.t3, flexShrink: 0 }} />
        )}
      </button>

      {/* Expanded reasoning steps */}
      {isExpanded && thoughts.length > 0 && (
        <div
          style={{
            paddingLeft: 18,
            paddingBottom: 4,
            borderLeft: `1.5px solid ${isStreaming ? C.gold + '25' : C.border}`,
            marginLeft: 5,
          }}
        >
          {thoughts.map((thought) => (
            <ThoughtStep
              key={thought.id}
              text={thought.text}
              isActive={thought.isActive}
              isError={thought.isError}
            />
          ))}
        </div>
      )}
    </div>
  );
}
